import React, { useState } from 'react';
import { createCustomer, fetchCustomers, updateCustomer } from '../../services/crmService';
import {
    autoMapColumns,
    ColumnMapping,
    extfTableToImportRows,
    ImportColumnKey,
    importRowsFromAi,
    isDatevExtfTable,
    mergeFieldDiffs,
    reconcileCustomerList,
    ReconciledCustomerRow,
    rowsToImportRows,
} from '../../services/customerListImport';
import { appendChangeEntry, diffCustomerChanges } from '../../services/customerChangeLog';
import { supabase } from '../../services/supabaseClient';
import { fileToTable } from '../../services/customerListFile';
import { extractCustomerList } from '../../services/geminiService';
import { rechtFehlerOder } from '../../services/rechte/rechtFehler';
import { Customer } from '../../types';
import { Button } from '../Button';
import { ImportMergeDialog } from './ImportMergeDialog';
import {
    AlertTriangle, ArrowRight, Check, CheckCircle2, FileSpreadsheet, GitMerge, Loader2,
    Upload, X,
} from 'lucide-react';

/**
 * Kundenliste hochladen (Steuerberater-Liste u. ae.): CSV/Excel mit
 * Spalten-Zuordnung, PDF ueber Gemini. Abgleich je Zeile ueber die
 * KUNDENNUMMER (Nummern-Treffer = vorhanden, egal wie der Name aussieht),
 * sonst Dublettenlogik. Nur angehakte neue Zeilen werden angelegt — mit der
 * Nummer aus der Liste; Umsaetze werden bewusst NICHT importiert (Umsatz
 * entsteht in der App aus Belegen).
 */
interface Props {
    onClose: () => void;
    onImported: () => void;
}

const MAX_PDF_BYTES = 18 * 1024 * 1024;

const fileToBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = () => reject(reader.error || new Error('Datei konnte nicht gelesen werden.'));
        reader.readAsDataURL(file);
    });

const FIELD_LABELS: Record<ImportColumnKey, string> = {
    customerNumber: 'Kundennummer',
    name: 'Name / Firma *',
    firstName: 'Vorname (Privatperson)',
    lastName: 'Nachname (Privatperson)',
    street: 'Straße',
    zip: 'PLZ',
    city: 'Ort',
    email: 'E-Mail',
    phone: 'Telefon',
    salutation: 'Anrede',
    title: 'Titel',
};

type Phase = 'select' | 'loading' | 'mapping' | 'review' | 'error';

export const CustomerListImportModal: React.FC<Props> = ({ onClose, onImported }) => {
    const [phase, setPhase] = useState<Phase>('select');
    const [error, setError] = useState<string | null>(null);
    const [fileName, setFileName] = useState('');

    // CSV/Excel-Weg
    const [headers, setHeaders] = useState<string[]>([]);
    const [rows, setRows] = useState<string[][]>([]);
    const [mapping, setMapping] = useState<ColumnMapping>({});
    // DATEV-EXTF: Spaltenzuordnung steht fest, die Zuordnungsphase entfaellt.
    const [extfDetected, setExtfDetected] = useState(false);

    // Abgleich
    const [reconciled, setReconciled] = useState<ReconciledCustomerRow[]>([]);
    const [selected, setSelected] = useState<Set<number>>(new Set());
    const [saving, setSaving] = useState(false);
    const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);

    // Merge-Dialog: Index der Zeile + Zielkunde (+ weitere Kandidaten zur Wahl);
    // erledigte Merges je Zeilenindex.
    const [mergeTarget, setMergeTarget] = useState<
        { index: number; existing: Customer; candidates?: Customer[] } | null
    >(null);
    const [mergedRows, setMergedRows] = useState<Set<number>>(new Set());
    const [allCustomers, setAllCustomers] = useState<Customer[]>([]);

    // Import-Ergebnis je Zeile: angelegte Zeilen sind erledigt, fehlgeschlagene
    // bleiben angehakt und tragen ihre Fehlermeldung an der Zeile.
    const [createdRows, setCreatedRows] = useState<Set<number>>(new Set());
    const [rowErrors, setRowErrors] = useState<Map<number, string>>(new Map());

    const fail = (msg: string) => {
        setError(msg);
        setPhase('error');
    };

    const handleFile = async (file: File) => {
        setFileName(file.name);
        setError(null);
        setExtfDetected(false);
        setPhase('loading');
        try {
            if (file.name.toLowerCase().endsWith('.pdf')) {
                if (file.size > MAX_PDF_BYTES) {
                    return fail('Das PDF ist größer als 18 MB — bitte verkleinern oder als CSV/Excel exportieren.');
                }
                const base64 = await fileToBase64(file);
                const parsed = await extractCustomerList(base64, file.type || 'application/pdf');
                const imported = importRowsFromAi(parsed);
                if (!imported.length) return fail('Im PDF wurden keine Kundenzeilen mit Namen erkannt.');
                await runReconcile(imported);
                return;
            }
            const table = await fileToTable(file);
            if (!table.headers.length || !table.rows.length) {
                return fail('Die Datei enthält keine erkennbare Tabelle (Kopfzeile + Datenzeilen).');
            }
            // DATEV-EXTF bringt seinen eigenen Spaltenkopf mit — die Zuordnung
            // steht damit fest und wird NICHT dem Nutzer vorgelegt.
            if (isDatevExtfTable(table.headers)) {
                const imported = extfTableToImportRows(table.headers, table.rows);
                if (!imported.length) {
                    return fail('Die DATEV-EXTF-Datei enthält keine Zeilen mit Namen.');
                }
                setExtfDetected(true);
                await runReconcile(imported);
                return;
            }
            setHeaders(table.headers);
            setRows(table.rows);
            setMapping(autoMapColumns(table.headers));
            setPhase('mapping');
        } catch (e) {
            fail(rechtFehlerOder(e));
        }
    };

    const runReconcile = async (imported: ReturnType<typeof rowsToImportRows>) => {
        setPhase('loading');
        try {
            const existing = await fetchCustomers({ includeArchived: true });
            setAllCustomers(existing);
            setMergedRows(new Set());
            setCreatedRows(new Set());
            setRowErrors(new Map());
            const rec = reconcileCustomerList(imported, existing);
            setReconciled(rec);
            setSelected(new Set(rec.map((r, i) => (r.status === 'new' ? i : -1)).filter((i) => i >= 0)));
            setPhase('review');
        } catch (e) {
            fail(rechtFehlerOder(e));
        }
    };

    /**
     * Feld-Auswahl anwenden: Update auf den Bestandskunden, Stammdaten-Aenderungen
     * historisieren (customerChangeLog). Die Zeile gilt danach als erledigt und
     * wird beim Import NICHT mehr angelegt.
     *
     * Basis ist IMMER der aktuelle Stand aus allCustomers, nie die Momentaufnahme
     * aus dem Abgleich: zeigen zwei Listenzeilen auf denselben Bestandskunden,
     * wuerde die zweite Zusammenfuehrung sonst gegen veraltete Werte diffen und
     * den Historien-Eintrag der ersten ueberschreiben.
     */
    const applyMerge = async (index: number, existing: Customer, updates: Partial<Customer>) => {
        const current = allCustomers.find((c) => c.id === existing.id) ?? existing;
        if (Object.keys(updates).length > 0) {
            const after = { ...current, ...updates };
            const changes = diffCustomerChanges(current, after);
            if (changes.length) {
                const { data: auth } = await supabase.auth.getUser();
                updates.changeLog = appendChangeEntry(
                    current.changeLog ?? [],
                    changes,
                    new Date().toISOString(),
                    auth.user?.email ?? undefined,
                );
            }
            await updateCustomer(current.id, updates);
            setAllCustomers((prev) => prev.map((c) => (c.id === current.id ? { ...c, ...updates } : c)));
        }
        setMergedRows((prev) => new Set(prev).add(index));
        setSelected((prev) => {
            const next = new Set(prev);
            next.delete(index);
            return next;
        });
        setMergeTarget(null);
    };

    const applyMapping = () => {
        // Vor-/Nachname reichen ebenso: daraus entsteht der Vollname.
        if (!mapping.name && !mapping.firstName && !mapping.lastName) {
            setError('Bitte mindestens die Spalte „Name / Firma" oder Vor-/Nachname zuordnen.');
            return;
        }
        setError(null);
        void runReconcile(rowsToImportRows(headers, rows, mapping));
    };

    /**
     * Anlegen der angehakten Zeilen — Fehler blockieren IMMER nur ihre eigene
     * Zeile. Frueher brach die erste kollidierende Nummer den ganzen Lauf ab,
     * die bereits angelegten Zeilen blieben angehakt und ein zweiter Versuch
     * lief sofort wieder in dieselbe Kollision.
     *
     * Erfolgreiche Zeilen werden sofort abgehakt und als „angelegt" markiert,
     * fehlgeschlagene bleiben angehakt und tragen ihre Meldung — ein erneuter
     * Klick verarbeitet damit genau die Fehlschlaege.
     */
    const handleImport = async () => {
        const toCreate = reconciled
            .map((r, index) => ({ index, row: r.row, status: r.status }))
            .filter(({ index }) => selected.has(index));
        setSaving(true);
        setError(null);
        setRowErrors(new Map());
        setProgress({ done: 0, total: toCreate.length });
        const errors = new Map<number, string>();
        let created = 0;
        for (const { index, row, status } of toCreate) {
            try {
                await createCustomer({
                    name: row.name,
                    // Nummernkonflikt: die Nummer der Liste gehoert einem anderen
                    // Kunden. Ohne Nummer anlegen — die Anlage vergibt dann die
                    // naechste freie aus dem Nummernkreis, statt an der
                    // Eindeutigkeit zu scheitern oder fremd zu ueberschreiben.
                    customerNumber: status === 'number_conflict' ? undefined : row.customerNumber,
                    address: row.address,
                    street: row.street,
                    zip: row.zip,
                    city: row.city,
                    email: row.email,
                    phone: row.phone,
                    customerType: row.customerType,
                    salutation: row.salutation,
                    title: row.title,
                    firstName: row.firstName,
                    lastName: row.lastName,
                    contacts: [],
                    // Kein Zahlungsziel aus der Liste: ohne Eintrag gilt die
                    // Grundeinstellung des Rechnungsmoduls.
                    defaultDiscountPercent: 0,
                });
                created += 1;
                setCreatedRows((prev) => new Set(prev).add(index));
                setSelected((prev) => {
                    const next = new Set(prev);
                    next.delete(index);
                    return next;
                });
            } catch (e) {
                errors.set(index, rechtFehlerOder(e));
            }
            setProgress({ done: created + errors.size, total: toCreate.length });
        }
        setSaving(false);
        if (!errors.size) {
            onImported();
            onClose();
            return;
        }
        // Teilerfolg: Liste im Hintergrund aktualisieren, Modal bleibt offen.
        if (created > 0) onImported();
        setRowErrors(errors);
        setProgress(null);
        setError(
            `${created} angelegt, ${errors.size} fehlgeschlagen — fehlgeschlagene Zeilen bleiben angehakt.`,
        );
    };

    const statusBadge = (r: ReconciledCustomerRow) => {
        switch (r.status) {
            case 'number_conflict':
                // Sichtbarer Text, kein Tooltip: dass eine Nummer schon an eine
                // ANDERE Firma vergeben ist, muss man beim Durchsehen der Liste
                // erkennen — frueher lief die Zeile still als „vorhanden" durch.
                return (
                    <div className="space-y-1">
                        <span className="text-[11px] font-bold bg-red-50 text-red-700 rounded px-2 py-0.5 inline-flex items-center gap-1">
                            <AlertTriangle className="w-3 h-3 shrink-0" /> Nummer vergeben an: {r.existing?.name}
                        </span>
                        <p className="text-[11px] text-slate-500 leading-snug">
                            Wird ohne diese Nummer angelegt (nächste freie Kundennummer).
                        </p>
                    </div>
                );
            case 'exists':
                return (
                    <span className="text-[11px] font-bold uppercase bg-emerald-50 text-emerald-600 rounded px-2 py-0.5 inline-flex items-center gap-1" title={`Kundennummer matcht ${r.existing?.name}`}>
                        <CheckCircle2 className="w-3 h-3" /> vorhanden{r.existing?.archivedAt ? ' (archiviert)' : ''}
                    </span>
                );
            case 'similar':
                return (
                    <span className="text-[11px] font-bold uppercase bg-amber-50 text-amber-700 rounded px-2 py-0.5 inline-flex items-center gap-1" title={r.hits?.map((h) => `${h.customer.name}: ${h.reasons.join(', ')}`).join(' | ')}>
                        <AlertTriangle className="w-3 h-3" /> ähnlich: {r.hits?.[0]?.customer.name}
                    </span>
                );
            case 'duplicate_in_file': {
                const what = r.duplicateReason === 'number' ? 'Nummer'
                    : r.duplicateReason === 'email' ? 'E-Mail'
                    : 'Name';
                const where = r.duplicateOfIndex !== undefined ? ` (Zeile ${r.duplicateOfIndex + 1})` : '';
                return (
                    <span className="text-[11px] font-bold uppercase bg-red-50 text-red-600 rounded px-2 py-0.5">
                        {what} doppelt in Datei{where}
                    </span>
                );
            }
            default:
                return <span className="text-[11px] font-bold uppercase bg-blue-50 text-blue-600 rounded px-2 py-0.5">neu</span>;
        }
    };

    // Nummernkonflikte sind anwaehlbar, aber NICHT vorausgewaehlt (runReconcile
    // hakt nur 'new' an): sie brauchen eine bewusste Entscheidung.
    const selectable = (r: ReconciledCustomerRow, i: number) =>
        !mergedRows.has(i) && !createdRows.has(i) &&
        (r.status === 'new' || r.status === 'similar' || r.status === 'number_conflict');
    const newCount = reconciled.filter((r) => r.status === 'new').length;
    const conflictCount = reconciled.filter((r) => r.status === 'number_conflict').length;

    /** Aktueller Stand eines Bestandskunden statt der Abgleichs-Momentaufnahme. */
    const live = (c: Customer): Customer => allCustomers.find((x) => x.id === c.id) ?? c;

    return (
        <div className="fixed inset-0 z-[110] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden animate-scale-in">
                <div className="bg-slate-900 text-white p-6 flex justify-between items-center shrink-0">
                    <div className="flex items-center gap-3">
                        <div className="bg-blue-500/20 p-2 rounded-xl border border-blue-500/30"><FileSpreadsheet className="w-6 h-6 text-blue-400" /></div>
                        <div>
                            <h3 className="font-bold text-lg">Kundenliste hochladen</h3>
                            <p className="text-xs text-slate-400">CSV, Excel oder PDF — Abgleich über die Kundennummer, Umsätze werden nicht importiert. Ähnliche Zeilen lassen sich feldweise mit dem Bestand zusammenführen.</p>
                        </div>
                    </div>
                    <button onClick={onClose} disabled={saving} className="hover:bg-white/10 p-2 rounded-full transition-all disabled:opacity-40"><X className="w-5 h-5" /></button>
                </div>

                <div className="flex-1 overflow-y-auto p-6">
                    {phase === 'select' && (
                        <label className="flex flex-col items-center justify-center gap-2 h-52 border-2 border-dashed border-slate-300 rounded-2xl cursor-pointer hover:border-blue-400 hover:bg-slate-50 transition-colors text-slate-400">
                            <Upload className="w-8 h-8" />
                            <span className="text-sm font-medium">Kundenliste wählen — CSV, Excel (.xlsx) oder PDF</span>
                            <span className="text-[11px]">z. B. Debitorenliste vom Steuerberater. Erwartete Spalten: Kundennummer, Name, Straße, PLZ, Ort, E-Mail, Telefon. DATEV-EXTF-Dateien werden automatisch erkannt.</span>
                            <input type="file" accept=".csv,.txt,.xlsx,.xls,.pdf" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) void handleFile(f); e.target.value = ''; }} />
                        </label>
                    )}

                    {phase === 'loading' && (
                        <div className="h-52 flex flex-col items-center justify-center gap-3 text-slate-500">
                            <Loader2 className="w-8 h-8 animate-spin" />
                            <span className="text-sm">{fileName} wird verarbeitet…</span>
                        </div>
                    )}

                    {phase === 'error' && (
                        <div className="h-52 flex flex-col items-center justify-center gap-3">
                            <AlertTriangle className="w-8 h-8 text-amber-500" />
                            <p className="text-sm text-slate-600 text-center max-w-md">{error}</p>
                            <Button variant="secondary" onClick={() => { setError(null); setPhase('select'); }}>Erneut versuchen</Button>
                        </div>
                    )}

                    {phase === 'mapping' && (
                        <div className="space-y-4">
                            <p className="text-sm text-slate-600">
                                Spalten aus <strong>{fileName}</strong> zuordnen. Automatisch erkannte Zuordnungen bitte prüfen — ohne Kundennummer kann der Abgleich nur über Name/Adresse laufen.
                            </p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                {(Object.keys(FIELD_LABELS) as ImportColumnKey[]).map((key) => (
                                    <div key={key}>
                                        <label className="block text-[11px] font-bold text-slate-400 uppercase mb-1">{FIELD_LABELS[key]}</label>
                                        <select
                                            value={mapping[key] ?? ''}
                                            onChange={(e) => setMapping({ ...mapping, [key]: e.target.value || undefined })}
                                            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500/20 bg-white"
                                        >
                                            <option value="">— nicht zugeordnet —</option>
                                            {headers.map((h) => (
                                                <option key={h} value={h}>{h}</option>
                                            ))}
                                        </select>
                                    </div>
                                ))}
                            </div>
                            {error && <p className="text-sm text-red-600">{error}</p>}
                            <div className="flex justify-end gap-3">
                                <Button variant="ghost" onClick={() => setPhase('select')}>Zurück</Button>
                                <Button variant="primary" onClick={applyMapping} icon={<ArrowRight className="w-4 h-4" />}>
                                    Abgleichen ({rows.length} Zeilen)
                                </Button>
                            </div>
                        </div>
                    )}

                    {phase === 'review' && extfDetected && (
                        <p className="mb-4 text-[11px] font-bold uppercase tracking-wider bg-blue-50 text-blue-700 rounded-lg px-3 py-2 inline-flex items-center gap-2">
                            <CheckCircle2 className="w-3.5 h-3.5" />
                            DATEV-EXTF erkannt — Spalten automatisch zugeordnet, Privatpersonen aus dem Namen getrennt
                        </p>
                    )}

                    {phase === 'review' && (
                        <table className="w-full text-left text-sm border border-slate-200 rounded-xl overflow-hidden">
                            <thead className="bg-slate-50 text-slate-500 font-bold uppercase text-[11px] tracking-wider">
                                <tr>
                                    <th className="p-3 w-10"></th>
                                    <th className="p-3 w-[110px]">Nummer</th>
                                    <th className="p-3">Name</th>
                                    <th className="p-3">Adresse</th>
                                    <th className="p-3 w-[220px]">Abgleich</th>
                                    <th className="p-3 w-[130px]"></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {reconciled.map((r, i) => {
                                    // Aktueller Stand des Bestandskunden: zeigen zwei Zeilen auf
                                    // denselben Kunden, muss die zweite gegen das Ergebnis der ersten
                                    // pruefen — sonst fehlt der Button genau dann, wenn er noetig ist.
                                    const liveExisting = r.existing ? live(r.existing) : undefined;
                                    return (
                                    <tr key={i} className={selected.has(i) ? 'bg-blue-50/50' : undefined}>
                                        <td className="p-3">
                                            <input
                                                type="checkbox"
                                                disabled={!selectable(r, i)}
                                                checked={selected.has(i)}
                                                onChange={(e) => {
                                                    const next = new Set(selected);
                                                    if (e.target.checked) next.add(i);
                                                    else next.delete(i);
                                                    setSelected(next);
                                                }}
                                                className="w-4 h-4 rounded border-slate-300 text-blue-600"
                                            />
                                        </td>
                                        <td className="p-3 font-mono">{r.row.customerNumber || <span className="text-slate-300">—</span>}</td>
                                        <td className="p-3 font-medium text-slate-800">{r.row.name}</td>
                                        <td className="p-3 text-slate-500 text-xs">{r.row.address || '—'}</td>
                                        <td className="p-3">
                                            {statusBadge(r)}
                                            {rowErrors.has(i) && (
                                                <p className="mt-1 text-[11px] text-red-600 leading-snug">{rowErrors.get(i)}</p>
                                            )}
                                        </td>
                                        <td className="p-3">
                                            {createdRows.has(i) ? (
                                                <span className="text-[11px] font-bold uppercase bg-emerald-50 text-emerald-600 rounded px-2 py-0.5 inline-flex items-center gap-1">
                                                    <CheckCircle2 className="w-3 h-3" /> angelegt
                                                </span>
                                            ) : mergedRows.has(i) ? (
                                                <span className="text-[11px] font-bold uppercase bg-emerald-50 text-emerald-600 rounded px-2 py-0.5 inline-flex items-center gap-1">
                                                    <CheckCircle2 className="w-3 h-3" /> zusammengeführt
                                                </span>
                                            ) : r.status === 'similar' && r.hits?.[0] ? (
                                                <Button
                                                    variant="secondary"
                                                    onClick={() => setMergeTarget({
                                                        index: i,
                                                        existing: live(r.hits![0].customer),
                                                        candidates: r.hits!.map((h) => live(h.customer)),
                                                    })}
                                                    icon={<GitMerge className="w-3.5 h-3.5" />}
                                                >
                                                    Zusammenführen
                                                </Button>
                                            ) : r.status === 'number_conflict' && liveExisting ? (
                                                // Ziel ist der Halter der Nummer; weitere Namens-Kandidaten
                                                // stehen im Dialog zur Wahl.
                                                <Button
                                                    variant="secondary"
                                                    onClick={() => setMergeTarget({
                                                        index: i,
                                                        existing: liveExisting,
                                                        candidates: [liveExisting, ...(r.hits ?? []).map((h) => live(h.customer))],
                                                    })}
                                                    icon={<GitMerge className="w-3.5 h-3.5" />}
                                                >
                                                    Zusammenführen
                                                </Button>
                                            ) : r.status === 'exists' && liveExisting && mergeFieldDiffs(r.row, liveExisting).length > 0 ? (
                                                <Button variant="secondary" onClick={() => setMergeTarget({ index: i, existing: liveExisting })} icon={<GitMerge className="w-3.5 h-3.5" />}>
                                                    Aktualisieren
                                                </Button>
                                            ) : null}
                                        </td>
                                    </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>

                {phase === 'review' && (
                    <div className="p-5 bg-slate-50 border-t flex flex-col sm:flex-row justify-between items-center gap-3 shrink-0">
                        <span className="text-xs text-slate-500">
                            {progress
                                ? `Lege an: ${progress.done}/${progress.total}…`
                                : `${selected.size} von ${reconciled.length} Zeilen werden angelegt (${newCount} neu erkannt). Ähnliche sind NICHT vorgemerkt — erst prüfen, dann anhaken, sonst entsteht eine zweite Kundennummer.${
                                    conflictCount > 0
                                        ? ` ${conflictCount} Zeile(n) mit Nummernkonflikt: die Nummer gehört einem anderen Kunden — angehakt werden sie ohne diese Nummer angelegt.`
                                        : ''
                                }`}
                        </span>
                        <div className="flex gap-3">
                            <Button variant="ghost" onClick={onClose} disabled={saving}>Abbrechen</Button>
                            <Button
                                variant="primary"
                                onClick={() => void handleImport()}
                                disabled={saving || selected.size === 0}
                                icon={saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                            >
                                {selected.size} Kunden anlegen
                            </Button>
                        </div>
                    </div>
                )}
                {phase === 'review' && error && (
                    <p className="px-6 pb-4 text-sm text-red-600 bg-slate-50">{error}</p>
                )}
            </div>

            {mergeTarget && (
                <ImportMergeDialog
                    row={reconciled[mergeTarget.index].row}
                    // Aktueller Stand statt Abgleichs-Momentaufnahme — sonst zeigt die
                    // Spalte „Bestand" nach einer vorherigen Zusammenfuehrung alte Werte.
                    existing={live(mergeTarget.existing)}
                    candidates={mergeTarget.candidates?.map(live)}
                    allCustomers={allCustomers}
                    // Zielkunde kommt aus dem Dialog zurueck: bei mehreren Kandidaten
                    // kann er dort umgestellt worden sein.
                    onApply={(updates, target) => applyMerge(mergeTarget.index, target, updates)}
                    onClose={() => setMergeTarget(null)}
                />
            )}
        </div>
    );
};
